import React from 'react';
import { Quote, Star, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { testimonials } from '../data/mock';
import { featuredTestimonial, secondTestimonial } from '../data/images';
import { trackCTAClick } from '../utils/analytics';

const Testimonials = () => {
  const scrollToConsultation = () => {
    trackCTAClick('Schedule a Strategy Session', 'testimonials');
    document.querySelector('#consultation')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="testimonials" className="py-16 sm:py-24 bg-slate-950" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12 sm:mb-16">
          <span className="text-emerald-400 text-sm font-semibold tracking-widest uppercase mb-4 block">
            Client Testimonials
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4 sm:mb-6">
            Real Results.
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-cyan-400">
              Real Impact.
            </span>
          </h2>
        </div>

        {/* Featured Testimonial Graphics */}
        <div className="grid md:grid-cols-2 gap-4 sm:gap-6 mb-10 sm:mb-12">
          <div className="relative overflow-hidden rounded-2xl group" data-testid="featured-testimonial-1">
            <img
              src={featuredTestimonial.image}
              alt={`${featuredTestimonial.author} testimonial`}
              loading="lazy"
              className="w-full aspect-[3/2] object-cover transition-transform duration-500 group-hover:scale-[1.02]"
            />
          </div>
          <div className="relative overflow-hidden rounded-2xl group" data-testid="featured-testimonial-2">
            <img
              src={secondTestimonial.image}
              alt={`${secondTestimonial.author} testimonial`}
              loading="lazy"
              className="w-full aspect-[3/2] object-cover transition-transform duration-500 group-hover:scale-[1.02]"
            />
          </div>
        </div>

        {/* Additional Testimonials Grid */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          {testimonials.map((testimonial, index) => (
            <div
              key={testimonial.author || testimonial.quote.slice(0, 32)}
              className="p-6 rounded-2xl bg-white/[0.02] border border-white/10 hover:border-emerald-500/30 transition-all duration-300"
              data-testid={`testimonial-card-${index}`}
            >
              <Quote className="h-8 w-8 text-emerald-500/30 mb-4" />
              <p className="text-slate-300 text-base mb-6 leading-relaxed italic">
                "{testimonial.quote}"
              </p>
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-white font-semibold text-sm">{testimonial.author}</div>
                  <div className="text-slate-500 text-xs">{testimonial.org}</div>
                </div>
                <div className="flex gap-0.5">
                  {['s1','s2','s3','s4','s5'].map((sk) => (
                    <Star key={sk} className="w-4 h-4 text-amber-400 fill-current" />
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Trust Indicators */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-6 rounded-2xl bg-white/[0.02] border border-white/10 mb-10">
          <div className="text-center">
            <div className="text-2xl sm:text-3xl font-bold text-white mb-1">500+</div>
            <div className="text-slate-400 text-xs sm:text-sm">Leaders Developed</div>
          </div>
          <div className="text-center">
            <div className="text-2xl sm:text-3xl font-bold text-white mb-1">100+</div>
            <div className="text-slate-400 text-xs sm:text-sm">Organizations Served</div>
          </div>
          <div className="text-center">
            <div className="text-2xl sm:text-3xl font-bold text-white mb-1">12+</div>
            <div className="text-slate-400 text-xs sm:text-sm">Years Experience</div>
          </div>
          <div className="text-center">
            <div className="text-2xl sm:text-3xl font-bold text-white mb-1">95%</div>
            <div className="text-slate-400 text-xs sm:text-sm">Client Satisfaction</div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <Button
            onClick={scrollToConsultation}
            data-testid="testimonials-cta"
            size="lg"
            className="bg-white text-slate-950 hover:bg-slate-100 font-semibold px-8 py-5 text-base transition-all duration-300 hover:shadow-xl hover:shadow-white/10"
          >
            Schedule a Strategy Session
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
